import { createAsyncThunk } from '@reduxjs/toolkit';
// @ts-ignore
import JSURL from 'jsurl';

import { JobDefinition } from 'actions/jobQueueActions';
import {
  makeThunkRequest,
  createApiRequestConfigWithRequestInfo,
  attachDatasetToPostData,
} from 'utils/thunkUtils';
import { ACTION } from 'actions/types';
import { DashboardStates } from 'reducers/rootReducer';
import { DashboardVariableMap } from 'types/dashboardTypes';
import { getUrlParamStringFromDashVars } from 'utils/dashboardUtils';
import {
  DownloadDataPanelBody,
  DownloadDataPanelSpreadsheetBody,
  ExportSpreadsheetType,
  ExportType,
} from 'actions/exportActions';
import { DataPanel, ResourceDataset } from 'types/exploResource';
import { AdHocOperationInstructions } from 'types/dataPanelTemplate';
import { REPORTED_ANALYTIC_ACTION_TYPES, UserTransformedSchema } from 'constants/types';
import { getViewableSchemaForPdf } from 'utils/dataPanelConfigUtils';
import { sendAnalyticsEvent } from '../analyticsThunks';
import { enqueueDashboardJobsThunk } from './jobsThunks';
import { DashboardLayoutThunk } from './types';
import { getTransformedDataPanelForCsv } from 'utils/csvDownloadUtils';
import { getArchetypeProperties } from 'reducers/selectors';
import { downloadExploreComputationSpreadsheet } from '../dashboardDataThunks/fetchFidoDataThunks';
import { useFidoForRequest } from 'utils/fido/fidoUtils';
import { getPrimarySortInfo } from 'utils/adHocUtils';

export const downloadDataPanelScreenshotThunk =
  (
    dataPanel: DataPanel,
    variables: DashboardVariableMap,
    adHocOperationInstructions: AdHocOperationInstructions,
    userTransformedSchema: UserTransformedSchema | undefined,
    email?: string,
    reportName?: string,
  ): DashboardLayoutThunk =>
  (dispatch, getState) => {
    const state = getState();
    const requestInfo = state.dashboardLayout.requestInfo;
    const dashboardTheme = state.dashboardStyles.dashboardTheme;
    const dataPanelData = state.dashboardData.dataPanelData[dataPanel.id];
    const archetypeProperties = getArchetypeProperties(state);

    const downloadFileName =
      dataPanel.visualize_op.generalFormatOptions?.export?.downloadFileName ||
      dataPanel.provided_id;

    const viewableSchema = getViewableSchemaForPdf(
      dataPanel,
      dataPanelData?.schema,
      userTransformedSchema,
    );

    const jobArgs: DownloadDataPanelBody = {
      data_panel_template_id: dataPanel.id,
      download_file_name: downloadFileName,
      version_number: requestInfo.versionNumber,
      variable_query: getUrlParamStringFromDashVars(
        {
          ...variables,
          reportName,
          userTransformedSchema: JSURL.stringify(viewableSchema),
          adHocOps: JSURL.stringify(adHocOperationInstructions),
          theme: dashboardTheme ?? undefined,
        },
        archetypeProperties,
      ),
      export_type: ExportType.PDF,
      email,
    };

    dispatch(sendAnalyticsEvent(REPORTED_ANALYTIC_ACTION_TYPES.DATA_PANEL_PDF_DOWNLOADED));

    // If email is passed in, we know we want to enqueue email job in backend so frontend does not have to keep track
    if (requestInfo.useJobQueue && email === undefined) {
      const job: JobDefinition = {
        job_type: ACTION.DOWNLOAD_DATA_PANEL_SCREENSHOT,
        job_args: jobArgs,
      };
      dispatch(enqueueDashboardJobsThunk({ jobs: [job] }));
    } else {
      dispatch(downloadDataPanelScreenshot(jobArgs));
    }
  };

export const downloadDataPanelScreenshot = createAsyncThunk<
  { url?: string },
  DownloadDataPanelBody,
  { state: DashboardStates }
>(ACTION.DOWNLOAD_DATA_PANEL_SCREENSHOT, async (args, { getState }) => {
  const requestInfo = getState().dashboardLayout.requestInfo;

  const urls = {
    embedUrl: `embed/export/data_panel_template/${args.export_type}/`,
    appUrl: `export/data_panel_template/${args.export_type}/`,
  };

  const requestConfig = createApiRequestConfigWithRequestInfo(urls, 'POST', requestInfo, args);

  return makeThunkRequest(requestConfig, 'Error downloading data panel');
});

export const downloadDataPanelSpreadsheetThunk =
  (
    dataPanel: DataPanel,
    variables: DashboardVariableMap,
    datasets: Record<string, ResourceDataset>,
    fileFormat: ExportSpreadsheetType,
    userTransformedSchema: UserTransformedSchema | undefined,
    email: string | undefined,
  ): DashboardLayoutThunk =>
  (dispatch, getState) => {
    const { dashboardLayout, dashboardData, fido } = getState();

    const requestInfo = dashboardLayout.requestInfo;
    const dataPanelData = dashboardData.dataPanelData[dataPanel.id];
    if (!dataPanelData) return;

    const transformedDataPanel = getTransformedDataPanelForCsv(
      dataPanel,
      dataPanelData.schema,
      userTransformedSchema,
    );

    const jobArgs: DownloadDataPanelSpreadsheetBody = {
      config: transformedDataPanel,
      id: transformedDataPanel.id,
      variables,
      sort_info: getPrimarySortInfo(dataPanelData.adHocOperationInstructions?.sortInfo),
      filter_info: dataPanelData.adHocOperationInstructions?.filterInfo,
      file_format: fileFormat,
      email,
      ...attachDatasetToPostData(transformedDataPanel, datasets, requestInfo),
    };

    dispatch(sendAnalyticsEvent(REPORTED_ANALYTIC_ACTION_TYPES.CSV_DOWNLOADED));

    if (useFidoForRequest(requestInfo, fido, datasets[transformedDataPanel.table_id])) {
      dispatch(
        downloadExploreComputationSpreadsheet({
          dataPanel: transformedDataPanel,
          dataset: datasets[transformedDataPanel.table_id],
          adHocInstructions: { sortInfo: jobArgs.sort_info, filterInfo: jobArgs.filter_info },
          fileFormat,
        }),
      );
    } else if (requestInfo.useJobQueue && email === undefined) {
      // If email is passed in, we know we want to enqueue email job in backend so frontend does not have to keep track
      const job: JobDefinition = {
        job_type: ACTION.DOWNLOAD_DATA_PANEL_SPREADSHEET,
        job_args: jobArgs,
      };
      dispatch(enqueueDashboardJobsThunk({ jobs: [job] }));
    } else {
      dispatch(downloadDataPanelSpreadsheet(jobArgs));
    }
  };

export const downloadDataPanelSpreadsheet = createAsyncThunk<
  { url?: string },
  DownloadDataPanelSpreadsheetBody,
  { state: DashboardStates }
>(ACTION.DOWNLOAD_DATA_PANEL_SPREADSHEET, async (args, { getState }) => {
  const requestInfo = getState().dashboardLayout.requestInfo;

  const urls = {
    embedUrl: `embed/download_spreadsheet/`,
    appUrl: `data_panel_templates/download_spreadsheet/`,
  };

  const requestConfig = createApiRequestConfigWithRequestInfo(urls, 'POST', requestInfo, args);

  return makeThunkRequest(requestConfig, 'Error downloading data panel');
});
